import * as React from 'react';
import { cn } from '@/shared/lib/utils';
import Label from '@/shared/components/Label';

interface InputProps extends React.ComponentProps<'input'> {
  icon?: React.ReactNode;
  trailingContent?: React.ReactNode;
  label?: React.ReactNode;
  labelClassName?: string;
  wrapperClassName?: string;
  onEnterPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  'data-testid'?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = 'text',
      icon,
      trailingContent,
      label,
      labelClassName,
      wrapperClassName,
      onEnterPress,
      onKeyDown,
      id,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const resolvedId = id ?? generatedId;

    const input = (
      <input
        ref={ref}
        id={resolvedId}
        type={type}
        data-th-typography="form-control"
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (!event.defaultPrevented && event.key === 'Enter') {
            onEnterPress?.(event);
          }
        }}
        className={cn(
          'h-11 w-full appearance-none rounded-xl border border-solid border-input-border bg-white px-4 text-form-control font-normal text-input-text placeholder:text-input-text/70 shadow-none ring-0 transition-[border-color,box-shadow] duration-200 ease-out outline-none focus:border-primary focus:shadow-none focus:ring-2 focus:ring-primary/20 focus-visible:border-primary focus-visible:shadow-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50',
          icon && 'pl-10',
          trailingContent && 'pr-12',
          className,
        )}
        {...props}
      />
    );

    const inputContent =
      !icon && !trailingContent ? (
        input
      ) : (
        <div className="relative">
          {icon ? (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-muted-foreground">
              {icon}
            </div>
          ) : null}
          {trailingContent ? (
            <div className="absolute inset-y-0 right-0 flex items-center pr-2">
              {trailingContent}
            </div>
          ) : null}
          {input}
        </div>
      );

    if (!label) {
      return inputContent;
    }

    return (
      <div className={cn('space-y-1.5', wrapperClassName)}>
        <Label htmlFor={resolvedId} className={labelClassName}>
          {label}
        </Label>
        {inputContent}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
